import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

const STORAGE_KEY = 'cgc:newsletter-popup';
const SESSION_SHOWN_KEY = 'cgc:newsletter-popup:shown';
const DAY_MS = 86_400_000;
const BLOCKED_RETRY_MS = 1500;

/**
 * Reads a storage area without throwing when storage is blocked.
 *
 * @param {'localStorage' | 'sessionStorage'} area
 * @returns {Storage | null}
 */
function getStorage(area) {
  try {
    const storage = window[area];
    const probe = `${STORAGE_KEY}:probe`;
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

/**
 * Newsletter signup popup. Owns timing, frequency, the native customer form
 * submission, and the welcome-reward state.
 *
 * Signup is a native form POST (Shopify's hCaptcha requires one). Liquid
 * renders the result into `data-posted` on the page Shopify redirects back
 * to, and the popup reopens in that state.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog.
 * @property {HTMLInputElement} [email] - The email input.
 * @property {HTMLButtonElement} [submit] - The submit button.
 * @property {HTMLElement} [error] - Inline error message container.
 * @property {HTMLElement} [status] - Polite live region for success and copy feedback.
 * @property {HTMLElement} [successHeading] - Heading focused when the reward appears.
 * @property {HTMLElement} [code] - The discount code text.
 * @property {HTMLImageElement[]} [images] - Popup images, preloaded before opening.
 *
 * @extends Component<Refs>
 */
class NewsletterPopup extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {number | undefined} */
  #timer;
  /** @type {Element | null} */
  #returnFocus = null;
  #submitting = false;
  /** Pending close animation; aborted when the popup reopens. @type {AbortController | null} */
  #closing = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const { dialog } = this.refs;

    dialog.addEventListener('cancel', this.#handleCancel, { signal });
    dialog.addEventListener('click', this.#handleBackdropClick, { signal });
    // Back/forward cache can restore the page mid-submit.
    window.addEventListener('pageshow', () => this.#setSubmitting(false), { signal });

    const { form } = this;
    if (form) {
      form.addEventListener('submit', this.handleSubmit, { signal });
      form.addEventListener('invalid', this.#handleInvalid, { signal, capture: true });
    }

    if (this.isDesignMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect, { signal });
      if (this.dataset.editorPreview !== 'off') this.open({ force: true });
      return;
    }

    this.#cleanPostedUrl();

    const posted = this.dataset.posted;

    if (posted === 'success') {
      this.#rememberSignup();
      this.open({ force: true });
      this.#showSuccess();
      return;
    }

    if (posted === 'error') {
      this.open({ force: true, focusEmail: true });
      return;
    }

    if (!this.#shouldSchedule()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timer = window.setTimeout(() => this.#openWhenClear(), delay);
    this.#preloadImages(delay);

    if (this.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.documentElement.addEventListener('mouseleave', this.#handleExitIntent, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    window.clearTimeout(this.#timer);
    unlockScroll(this.refs.dialog);
  }

  /** The native customer form. Liquid's form tag can't carry a `ref`. */
  get form() {
    return this.querySelector('form.newsletter-popup__form');
  }

  get isDesignMode() {
    return this.dataset.designMode === 'true';
  }

  /**
   * Opens the popup.
   *
   * @param {{ force?: boolean, focusEmail?: boolean }} [options] - `force` skips the overlay check.
   */
  open({ force = false, focusEmail = false } = {}) {
    const { dialog } = this.refs;
    if (this.#closing) {
      // Reopened mid-close: keep the dialog open and drop the pending close.
      this.#closing.abort();
      this.#closing = null;
      dialog.classList.remove('newsletter-popup__dialog--closing');
      return;
    }
    if (dialog.open) return;
    if (!force && this.#isBlocked()) return;

    window.clearTimeout(this.#timer);
    this.#returnFocus = document.activeElement;

    if (!this.isDesignMode) getStorage('sessionStorage')?.setItem(SESSION_SHOWN_KEY, '1');

    lockScroll(dialog);
    dialog.showModal();
    dialog.classList.remove('newsletter-popup__dialog--closing');

    // Start on the dialog itself so mobile keyboards don't pop up unprompted.
    if (focusEmail) this.refs.email?.focus();
    else dialog.focus({ preventScroll: true });
  }

  /**
   * Switches lazy popup images to eager loading shortly before the popup can
   * open, so the image is ready without competing with the initial page load.
   *
   * @param {number} delay - Milliseconds until the popup is due.
   */
  #preloadImages(delay) {
    const load = () => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const hidden = mobile
        ? this.classList.contains('newsletter-popup--no-mobile-media')
        : this.classList.contains('newsletter-popup--media-hidden');
      if (hidden) return;
      for (const image of this.refs.images ?? []) image.loading = 'eager';
    };
    const start = () => window.setTimeout(load, Math.max(0, delay - 2000));
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true, signal: this.#listeners?.signal });
  }

  /**
   * Closes the popup and starts the dismissal cooldown.
   */
  close = () => {
    const { dialog } = this.refs;
    if (!dialog.open || this.#closing) return;

    if (!this.isDesignMode && this.dataset.state !== 'success') this.#rememberDismissal();

    const finish = () => {
      this.#closing?.abort();
      this.#closing = null;
      dialog.close();
      dialog.classList.remove('newsletter-popup__dialog--closing');
      unlockScroll(dialog);
      const returnTo = this.#returnFocus;
      if (returnTo instanceof HTMLElement && returnTo.isConnected && returnTo !== document.body) {
        returnTo.focus({ preventScroll: true });
      } else if (document.activeElement instanceof HTMLElement && dialog.contains(document.activeElement)) {
        // Nothing to return to (popup opened on a timer); don't leave focus on a hidden control.
        document.activeElement.blur();
      }
      this.#returnFocus = null;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    this.#closing = new AbortController();
    const { signal } = this.#closing;
    dialog.classList.add('newsletter-popup__dialog--closing');
    dialog.addEventListener(
      'animationend',
      (event) => {
        if (event.target === dialog) finish();
      },
      { signal }
    );
    // Fallback in case the closing animation never runs.
    const fallback = window.setTimeout(finish, 400);
    signal.addEventListener('abort', () => window.clearTimeout(fallback));
  };

  /**
   * Lets the native customer form submission go ahead once the email is
   * valid. Shopify's hCaptcha binds to this form and needs a real browser
   * submit, so the result comes back as a page load that Liquid renders into
   * `data-posted` and the popup reopens in the matching state.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    const { email } = this.refs;
    if (!email) return;

    if (this.#submitting || this.dataset.state === 'success') {
      event.preventDefault();
      return;
    }

    email.value = email.value.trim();
    if (!email.checkValidity()) {
      event.preventDefault();
      this.#showError(this.#validationMessage(email));
      return;
    }

    if (this.isDesignMode) {
      event.preventDefault();
      this.#showSuccess();
      return;
    }

    this.#clearError();
    this.#setSubmitting(true);
    this.#watchForChallenge();
    // If the browser never navigates (challenge abandoned), let the shopper retry.
    window.setTimeout(() => this.isConnected && this.#setSubmitting(false), 20_000);
  };

  /**
   * Replaces the browser's validation bubble with the inline message.
   *
   * @param {Event} event
   */
  #handleInvalid = (event) => {
    const { email } = this.refs;
    if (event.target !== email || !email) return;
    event.preventDefault();
    this.#showError(this.#validationMessage(email));
  };

  /**
   * @param {HTMLInputElement} email
   * @returns {string | undefined}
   */
  #validationMessage(email) {
    return email.validity.valueMissing ? this.dataset.errorRequired : this.dataset.errorInvalid;
  }

  /**
   * hCaptcha shows its challenge outside the popup. A modal dialog would make
   * it inert, so drop to a non-modal dialog while a challenge is on screen.
   */
  #watchForChallenge() {
    const started = Date.now();
    const check = () => {
      const { dialog } = this.refs;
      if (!this.isConnected || !dialog.open || Date.now() - started > 60_000) return;

      const challenge = [...document.querySelectorAll('iframe[src*="hcaptcha"]')].some((frame) => {
        const rect = frame.getBoundingClientRect();
        return rect.width > 200 && rect.height > 200 && !dialog.contains(frame);
      });

      if (challenge && dialog.matches(':modal')) {
        dialog.close();
        dialog.show();
        dialog.classList.add('newsletter-popup__dialog--challenge');
        unlockScroll(dialog);
        return;
      }

      this.#timer = window.setTimeout(check, 300);
    };
    this.#timer = window.setTimeout(check, 300);
  }

  /**
   * Copies the discount code and announces the result.
   *
   * @param {MouseEvent} event
   */
  copyCode = async (event) => {
    const button = event.target;
    const code = this.refs.code?.textContent?.trim();
    if (!code || !(button instanceof HTMLButtonElement)) return;

    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch {
      copied = this.#legacyCopy(code);
    }

    const message = copied ? this.dataset.copiedMessage : this.dataset.copyFailedMessage;
    this.#announce(message ?? '');

    const label = button.querySelector('[data-copy-label]');
    if (!copied || !(label instanceof HTMLElement)) return;

    const original = button.dataset.copyLabel ?? label.textContent ?? '';
    button.dataset.copyLabel = original;
    label.textContent = this.dataset.copiedLabel ?? original;
    button.dataset.copied = 'true';
    window.setTimeout(() => {
      if (!button.isConnected) return;
      label.textContent = original;
      delete button.dataset.copied;
    }, 2500);
  };

  /**
   * Clears the inline error once the shopper edits the email.
   */
  handleInput = () => {
    if (this.refs.email?.getAttribute('aria-invalid') === 'true') this.#clearError();
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    if (this.dataset.backdropClose !== 'true' || event.target !== this.refs.dialog) return;
    this.close();
  };

  /** @param {MouseEvent} event */
  #handleExitIntent = (event) => {
    if (event.clientY > 0 || event.relatedTarget) return;
    document.documentElement.removeEventListener('mouseleave', this.#handleExitIntent);
    this.#openWhenClear();
  };

  /** @param {Event} event */
  #handleEditorSelect = (event) => {
    if (!(event instanceof CustomEvent)) return;
    // Step aside while another section is being edited so the preview stays usable.
    if (event.detail?.sectionId === this.dataset.sectionId) this.open({ force: true });
    else this.close();
  };

  /** @param {Event} event */
  #handleEditorDeselect = (event) => {
    if (!(event instanceof CustomEvent) || event.detail?.sectionId !== this.dataset.sectionId) return;
    if (this.dataset.editorPreview === 'off') this.close();
  };

  /**
   * Opens now, or waits until other overlays (drawers, dialogs, consent
   * banners, Shopify Forms popups) have closed.
   */
  #openWhenClear = () => {
    window.clearTimeout(this.#timer);
    if (this.refs.dialog.open || !this.#shouldSchedule()) return;

    if (this.#isBlocked() || document.visibilityState !== 'visible') {
      this.#timer = window.setTimeout(this.#openWhenClear, BLOCKED_RETRY_MS);
      return;
    }

    this.open();
  };

  #shouldSchedule() {
    if (getStorage('sessionStorage')?.getItem(SESSION_SHOWN_KEY)) return false;

    const record = this.#readRecord();
    const now = Date.now();
    if (record.subscribedUntil && record.subscribedUntil > now) return false;
    if (record.dismissedUntil && record.dismissedUntil > now) return false;
    return true;
  }

  #isBlocked() {
    const { dialog } = this.refs;
    const openDialog = [...document.querySelectorAll('dialog[open]')].some((element) => element !== dialog);
    if (openDialog) return true;
    if (document.querySelector('theme-drawer[open], details[open][scroll-lock]')) return true;

    // Shopify's cookie banner is position: fixed, so offsetParent can't be used.
    const privacyBanner = document.getElementById('shopify-pc__banner');
    if (privacyBanner && privacyBanner.getClientRects().length > 0) {
      const { visibility, opacity } = getComputedStyle(privacyBanner);
      if (visibility !== 'hidden' && opacity !== '0') return true;
    }

    for (const embed of document.querySelectorAll('shopify-forms-embed')) {
      const overlay = embed.shadowRoot?.querySelector('[role="dialog"], dialog[open]');
      if (overlay instanceof HTMLElement && overlay.getClientRects().length > 0) return true;
    }

    return false;
  }

  /** @returns {{ dismissedUntil?: number, subscribedUntil?: number }} */
  #readRecord() {
    try {
      const value = JSON.parse(getStorage('localStorage')?.getItem(STORAGE_KEY) ?? '{}');
      return value && typeof value === 'object' ? value : {};
    } catch {
      return {};
    }
  }

  /** @param {{ dismissedUntil?: number, subscribedUntil?: number }} update */
  #writeRecord(update) {
    if (this.isDesignMode) return;
    try {
      getStorage('localStorage')?.setItem(STORAGE_KEY, JSON.stringify({ ...this.#readRecord(), ...update }));
    } catch {
      // Storage full or blocked; the session flag still limits repeats.
    }
  }

  #rememberDismissal() {
    const days = Math.max(0, Number(this.dataset.dismissDays) || 0);
    this.#writeRecord({ dismissedUntil: Date.now() + days * DAY_MS });
  }

  #rememberSignup() {
    const days = Math.max(0, Number(this.dataset.signupDays) || 0);
    this.#writeRecord({ subscribedUntil: Date.now() + days * DAY_MS });
  }

  #showSuccess() {
    this.dataset.state = 'success';
    this.#announce(this.dataset.successMessage ?? '');
    requestAnimationFrame(() => this.refs.successHeading?.focus());
  }

  /** @param {string | undefined} message */
  #showError(message) {
    const { error, email } = this.refs;
    if (!error || !email) return;

    error.textContent = message || this.dataset.errorGeneric || '';
    email.setAttribute('aria-invalid', 'true');
    email.focus();
  }

  #clearError() {
    const { error, email } = this.refs;
    if (error) error.textContent = '';
    email?.removeAttribute('aria-invalid');
  }

  /** @param {boolean} submitting */
  #setSubmitting(submitting) {
    this.#submitting = submitting;
    const { submit } = this.refs;
    const { form } = this;
    form?.toggleAttribute('aria-busy', submitting);
    if (!submit) return;
    submit.setAttribute('aria-disabled', String(submitting));
    submit.dataset.loading = String(submitting);
  }

  /** Removes Shopify's `customer_posted` marker so a refresh doesn't replay the result. */
  #cleanPostedUrl() {
    const url = new URL(window.location.href);
    const formId = this.form?.id;
    const hadMarker = url.searchParams.has('customer_posted');
    url.searchParams.delete('customer_posted');
    if (formId && url.hash === `#${formId}`) url.hash = '';
    if (hadMarker || url.href !== window.location.href) {
      window.history.replaceState(window.history.state, '', url);
    }
  }

  /** @param {string} message */
  #announce(message) {
    const { status } = this.refs;
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 50);
  }

  /**
   * @param {string} text
   * @returns {boolean}
   */
  #legacyCopy(text) {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    this.refs.dialog.append(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    }
    field.remove();
    return copied;
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
